'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function IsikDolgusununGozAltiMorluklarinaEtkisi() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/isik-dolgusunun-goz-alti-morluklarina-etkisi';
  const pageTitle = 'Işık Dolgusunun Göz Altı Morluklarına Etkisi';
  const pageDescription = 'Göz altı morlukları için ışık dolgusu ne kadar etkilidir? Sebeplerine göre değişen sonuçlar, etki süresi ve uzman önerileri.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Işık Dolgusunun Göz Altı Morluklarına Etkisi', href: '/tr/blog/isik-dolgusunun-goz-alti-morluklarina-etkisi' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Işık dolgusunun göz altı morluklarına etkisi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-cyan-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Göz Altı Morlukları
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Işık Dolgusunun Göz Altı Morluklarına Etkisi
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>27 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>6 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Göz altı morlukları, kişiye yorgun, üzgün ve yaşlı bir ifade verir.
                Kapatıcılarla geçici çözümler mümkün olsa da, ışık dolgusu bu soruna medikal bir yaklaşım sunar.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Ama her morluk tipi aynı değildir; bu yüzden etki oranı da kişiden kişiye değişir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Göz Altı Morluklarının Türleri
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-cyan-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Morluk Türü</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Sebep</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Renk</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Çözüm</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Pigmentasyon kaynaklı</td>
                      <td className="border border-gray-300 px-4 py-3">Melanin artışı</td>
                      <td className="border border-gray-300 px-4 py-3">Kahverengi</td>
                      <td className="border border-gray-300 px-4 py-3">Kimyasal peeling, lazer</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Damar kaynaklı</td>
                      <td className="border border-gray-300 px-4 py-3">Yüzeysel damarlar</td>
                      <td className="border border-gray-300 px-4 py-3">Mavi / Mor</td>
                      <td className="border border-gray-300 px-4 py-3">Işık dolgusu etkili</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Çukur kaynaklı (gölge)</td>
                      <td className="border border-gray-300 px-4 py-3">Cilt altı yağ kaybı</td>
                      <td className="border border-gray-300 px-4 py-3">Gri</td>
                      <td className="border border-gray-300 px-4 py-3">Işık dolgusu en etkili</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Karma tip</td>
                      <td className="border border-gray-300 px-4 py-3">Birden fazla sebep</td>
                      <td className="border border-gray-300 px-4 py-3">Değişken</td>
                      <td className="border border-gray-300 px-4 py-3">Kombine tedavi gerekir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                <p className="text-blue-800 font-medium">
                  💬 Işık dolgusu, özellikle damar ve çukur kaynaklı morluklarda etkili olur.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Işık Dolgusunun Etki Mekanizması
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Işık dolgusu, hyaluronik asit ve vitamin kombinasyonudur.
              </p>

              <div className="bg-cyan-50 rounded-xl p-6 mb-6">
                <p className="text-cyan-800 mb-4 font-medium">
                  Bu sayede:
                </p>
                <ul className="list-disc list-inside space-y-2 text-cyan-800">
                  <li>Cilt altı boşluğu doldurur (gölge azalır)</li>
                  <li>Kan dolaşımını düzenler (rengi açar)</li>
                  <li>Işığı daha iyi yansıtarak "aydınlık efekt" oluşturur</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Uygulama Alanı ve Derinliği
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <p className="text-green-800 mb-4">
                  Uygulama, orbital rim (göz çukuru kemiği) hattı boyunca yapılır.
                </p>
                <p className="text-green-800 mb-4">
                  Mikro kanül tekniğiyle dolgu damar ve kas tabakasının üstüne bırakılır.
                </p>
                <p className="text-green-800">
                  Bu, hem güvenli hem de pürüzsüz bir sonuç sağlar.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Etki Süresi ve Kalıcılık
              </h2>
              
              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-800 mb-2">📅</div>
                    <p className="text-purple-800 font-medium">Etkisi anında fark edilir</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-800 mb-2">⏰</div>
                    <p className="text-purple-800 font-medium">Tam sonuç: 7–10 gün sonra</p>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-800 mb-2">🕐</div>
                    <p className="text-purple-800 font-medium">Kalıcılık: 12–18 ay</p>
                  </div>
                </div>
                <div className="bg-purple-100 rounded-lg p-4 mt-4">
                  <p className="text-purple-900">
                    Yine de göz çevresi hareketli bir bölge olduğu için kişisel faktörler sonucu etkileyebilir.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Riskler ve Yan Etkiler
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-orange-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Yan Etki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Süre</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hafif morarma</td>
                      <td className="border border-gray-300 px-4 py-3">Kanüllerin geçtiği alanda olabilir</td>
                      <td className="border border-gray-300 px-4 py-3">2–3 gün</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Şişlik / ödem</td>
                      <td className="border border-gray-300 px-4 py-3">Geçici reaksiyon</td>
                      <td className="border border-gray-300 px-4 py-3">48 saat</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Tyndall etkisi (mavi yansıma)</td>
                      <td className="border border-gray-300 px-4 py-3">Çok yüzeysel uygulamada olur</td>
                      <td className="border border-gray-300 px-4 py-3">Uzman düzeltir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
                <p className="text-green-800 font-medium">
                  Bu yan etkiler, uzman hekim uygulamasıyla neredeyse sıfıra iner.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Sonuçların Doğallığı
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Işık dolgusu, doğru kişide dramatik bir değişim yaratmadan "dinlenmiş yüz ifadesi" sağlar.
                Fotoğraf filtresi kullanmadan bile cilt daha parlak görünür.
              </p>

              <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 mb-6">
                <p className="text-indigo-800 font-medium">
                  💬 "Yorgun görünüyorsun." cümlesini tarihe gömmek için doğru işlemdir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. Etki Oranları ve Beklentiler
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-green-50 rounded-xl p-6">
                  <h4 className="font-semibold text-green-900 mb-4 flex items-center">
                    <span className="text-green-600 mr-2">✅</span>
                    Yüksek Etki (%80-90)
                  </h4>
                  <ul className="space-y-2 text-green-800">
                    <li>• Çukur kaynaklı morluklar</li>
                    <li>• Damar kaynaklı morluklar</li>
                    <li>• Hafif pigmentasyon</li>
                    <li>• Genç yaş grubu (25-35)</li>
                  </ul>
                </div>
                
                <div className="bg-yellow-50 rounded-xl p-6">
                  <h4 className="font-semibold text-yellow-900 mb-4 flex items-center">
                    <span className="text-yellow-600 mr-2">⚠️</span>
                    Orta Etki (%40-60)
                  </h4>
                  <ul className="space-y-2 text-yellow-800">
                    <li>• Karma tip morluklar</li>
                    <li>• Orta düzey pigmentasyon</li>
                    <li>• Yaşlı cilt yapısı</li>
                    <li>• Genetik faktörler</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8. Fiyat Bilgisi
              </h2>
              
              <div className="bg-cyan-50 rounded-xl p-6 mb-6">
                <div className="text-center">
                  <h4 className="font-semibold text-cyan-900 mb-2">Göz Altı Işık Dolgusu</h4>
                  <p className="text-3xl font-bold text-cyan-800 mb-2">₺3.500 - ₺4.500</p>
                  <p className="text-cyan-700 text-sm">Teosyal Redensity II ile</p>
                  <p className="text-cyan-700 text-sm mt-2">
                    * Fiyatlar 2026 yılı için geçerlidir. Detaylı fiyat bilgisi için iletişime geçin.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Uzman Görüşü
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Işık dolgusu, morlukları kapatmak yerine ışığı yeniden yönlendirir.
                  Bu sayede gölgeler azalır, cilt tonu eşitlenir.
                  Fakat yoğun pigmentasyon varsa dolgu tek başına yeterli olmaz."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Işık dolgusu morlukları tamamen geçirir mi?</h3>
                  <p className="text-gray-600">Pigmentasyon kaynaklı morluklarda tam değil, %40–60 oranında iyileşme sağlar.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Etki hemen görülür mü?</h3>
                  <p className="text-gray-600">Evet, ilk gün fark edilir; tam sonuç 1 haftada oturur.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Kalıcılık süresi ne kadar?</h3>
                  <p className="text-gray-600">12–18 ay.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Klasik dolgu ile farkı nedir?</h3>
                  <p className="text-gray-600">Daha ince yapılıdır, göz altı için özel geliştirilmiştir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Hangi morluk türünde en etkili?</h3>
                  <p className="text-gray-600">Çukur kaynaklı (gölge) ve damar kaynaklı morluklarda en etkilidir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Ağrılı bir işlem midir?</h3>
                  <p className="text-gray-600">Hayır, anestezik kremle birlikte rahatsızlık hissi minimumdur.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>ISAPS (2024) – "Tear Trough Correction and Hyaluronic Fillers"</li>
                  <li>Teoxane Clinical Trials (2023) – "Redensity II and Under-Eye Circulation"</li>
                  <li>Harvard Health (2024) – "Understanding Dark Circles and Their Treatments"</li>
                  <li>TETD (2024) – "Işık Dolgusu Uygulama Rehberi"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#ışıkdolgu</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#gözaltımorluk</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#teosyal</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-cyan-100 text-cyan-800 px-3 py-1 rounded-full text-sm">#gözaltı</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-cyan-600 to-blue-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Göz Altı Morlukları İçin Işık Dolgusu Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan göz altı morlukları ve ışık dolgusu tedavisi hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Göz altı morlukları için ışık dolgusu hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-cyan-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/goz-alti-isik-dolgusuna-uygunluk-kimler-icin-ideal-kimler-icin-degil"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Göz Altı Işık Dolgusuna Uygunluk</h4>
              <p className="text-gray-600 text-sm">Göz altı ışık dolgusu herkese yapılabilir mi? Uygun aday kriterleri ve riskli durumlar.</p>
            </a>
            <a
              href="/tr/blog/cilt-kurulugunda-mezoterapi-derin-nem-destegi-ve-hucresel-yenilenme"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Cilt Kuruluğunda Mezoterapi</h4>
              <p className="text-gray-600 text-sm">Cilt kuruluğuna karşı mezoterapi nasıl etki eder? Derin nem desteği ve hücresel yenilenme.</p>
            </a>
            <a
              href="/tr/blog/mezoterapi-etkileri-ne-zaman-gorulur"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Mezoterapinin Etkileri Ne Zaman Görülür?</h4>
              <p className="text-gray-600 text-sm">Mezoterapi uygulamasından sonra etkilerin ne zaman görülmeye başladığı hakkında bilgiler.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-cyan-600 text-white rounded-full hover:bg-cyan-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
